import angular from 'angular';
import { isHistorical } from '@sdk/unavailabilities/helpers/utils';

import availabilityRequestedModalHtml from '../../shifts/components/unavailabilityConfirmationModal.html';
import { MODAL_CONFIRMATION_STATE_DELAY } from '../../constants';

class AvailabilityUtils {
  constructor(
    Authentication,
    Restangular,
    Utils,
    EventCollection,
    SlingApi,
    dateFilter,
    extendCalendarModel,
    sdk,
    $q,
    $uibModal,
    $rootScope
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      Restangular,
      Utils,
      EventCollection,
      SlingApi,
      dateFilter,
      extendCalendarModel,
      sdk,
      $q,
      $uibModal,
      $rootScope,
    });
    Object.assign(this, {
      unavailabilityUtils: sdk('unavailabilityUtils'),
    });
  }

  async saveAvailability(availabilitySets) {
    await this.$q.all(
      availabilitySets.map(async (set) => {
        const { availabilityId, _edited } = set;
        const payload = this.getUnavailabilityPayload(set);

        const deleteEmpty = set.isEmpty();
        const response = await this.updateAvailabilityEntry(
          availabilityId,
          deleteEmpty,
          _edited,
          payload
        );

        this.processUnavailabilitySetResponse(set, response);

        return true;
      })
    );

    this.refreshUnavailabilities(availabilitySets);
  }

  refreshUnavailabilities(sets) {
    this.EventCollection.syncEvents();
    this.resetAvailabilitySets(sets);
    this.$rootScope.$broadcast('events:sync');
  }

  processUnavailabilitySetResponse(set, response) {
    if (response && !response.deleted) {
      Object.assign(set, { availabilityId: response.id }, this._mapAvailabilitySet(response));

      if ('reset' in set) {
        set.reset();
      }

      if (!this.Authentication.user.isManager && !response.approved && this.isApprovalEnabled()) {
        this.showRequestConfirmModal(
          'Unavailability has been requested',
          'It will take effect once your manager approves it.',
          'pending'
        );
      }
    } else if (response && response.deleted) {
      Object.assign(set, { availabilityId: null });
    }
  }

  getUnavailabilityPayload(set) {
    const { availabilityId, unavailabilities, details } = set;
    let availabilities = this.unavailabilityUtils.flattenUnavailabilites(
      angular.copy(unavailabilities),
      details.start
    );
    availabilities = this.unavailabilityUtils.splitUnavailabilities(availabilities);
    availabilities.forEach((a) => {
      // Send timezone-less times!
      a.dtstart = this.dateFilter(a.dtstart, 'yyyy-MM-ddTHH:mm');
      a.dtend = this.dateFilter(a.dtend, 'yyyy-MM-ddTHH:mm');
    });

    const payload = {
      ...details,
      availabilities,
      id: availabilityId,
      until: this.dateFilter(details.until, 'yyyy-MM-dd'),
      start: this.dateFilter(details.start, 'yyyy-MM-ddTHH:mm+00:00'),
    };

    return payload;
  }

  async updateAvailabilityEntry(id, deleted, editing, payload) {
    let result;
    if (id && !deleted && editing) {
      result = await this.SlingApi.updateUnavailability(payload);
    } else if (id && deleted) {
      await this.SlingApi.deleteUnavailability(id);
      return { deleted: true };
    } else if (!id && !deleted) {
      result = await this.SlingApi.createUnavailability(payload);
    } else {
      // created new set end deleted it before saving - do nothing.
      return null;
    }

    return this.Restangular.restangularizeElement(null, result, 'availability');
  }

  async getAvailabilities(user) {
    const availabilitySets = await this.SlingApi.getUnavailabilities(user.isMe ? null : user.id);
    const availabilitySetsDetails = await this.$q.all(
      availabilitySets.map((uSet) => this.getAvailabilityDetails(uSet.id))
    );

    return availabilitySetsDetails.map((availabilitySet) => {
      const result = {
        availabilityId: availabilitySet.id,
      };

      Object.assign(result, this._mapAvailabilitySet(availabilitySet));

      result.reset();
      return result;
    });
  }
  a;

  async getUnavailabilitiesCount(userId) {
    const allSets = await this.SlingApi.getUnavailabilities(userId);
    return allSets?.filter((s) => !isHistorical(s)).length;
  }

  async getSingleUnavailabilitySet(unavailabilitySetId) {
    const setDetails = await this.getAvailabilityDetails(unavailabilitySetId);
    return this.getUnavailabilitySetToDisplay(setDetails);
  }

  async getAvailabilityDetails(setId) {
    const setWithDetails = await this.SlingApi.getUnavailabilitySetDetails(setId);
    return this.Restangular.restangularizeElement(null, setWithDetails, 'availability');
  }

  getUnavailabilitySetToDisplay(set) {
    const result = { availabilityId: set.id };
    Object.assign(result, this._mapAvailabilitySet(set));
    result.reset();

    return result;
  }

  createAvailabilitySet(forUser, copyFromSet, defaultName = '') {
    const defaultInterval = this.sdk('constants').UNAVAILABILITY_NO_REPEAT_VALUE;

    const availabilities = copyFromSet
      ? this.unavailabilityUtils.flattenUnavailabilites(angular.copy(copyFromSet.unavailabilities))
      : [];
    const byday = copyFromSet ? copyFromSet.details.byday : '';
    const freq = copyFromSet ? copyFromSet.details.freq : 'WEEKLY';
    const interval = copyFromSet ? copyFromSet.details.interval : defaultInterval;
    const name = copyFromSet ? copyFromSet.details.name : defaultName;
    const until =
      copyFromSet && copyFromSet.details.until
        ? this.Utils.getRealDate(copyFromSet.details.until)
        : null;
    const start =
      copyFromSet && copyFromSet.details.start
        ? this.Utils.getRealDate(copyFromSet.details.start)
        : new Date();
    const user = { id: forUser.id };

    const set = this._mapAvailabilitySet({
      availabilities,
      byday,
      freq,
      interval,
      name,
      until,
      start,
      user,
    });

    set.reset();
    set._editing = true;
    set._edited = true;
    return set;
  }

  resetAvailabilitySets(sets = []) {
    if (sets.length) {
      for (let i = sets.length - 1; i >= 0; i--) {
        if (!sets[i].availabilityId) {
          sets.splice(i, 1);
        }
      }
    }

    sets.forEach((item) => item.reset());
  }

  _mapAvailabilitySet(set) {
    const self = this;
    const { byday, freq, interval, name, until, start, user, approved, cancelled } = set;
    const _unavailabilitiesArray = this.unavailabilityUtils
      .getAvailabilityArray(set)
      .map((singleAvailability) => ({
        ...singleAvailability,
        dtstart: this.Utils.getRealDate(singleAvailability.dtstart),
        dtend: this.Utils.getRealDate(singleAvailability.dtend),
      }));
    const { visibleHoursStart, visibleHoursEnd } = this.Authentication.user.orgSettings;
    const params = { visibleHoursStart, visibleHoursEnd, extendModel: true, dayNamesAsKeys: true };
    const _originalUnavailabilities = this.unavailabilityUtils.getUnavailabilitiesByDay(
      _unavailabilitiesArray,
      params
    );

    const _originalDetails = {
      approved,
      cancelled,
      byday,
      freq,
      interval,
      name,
      user,
      until: until ? this.Utils.getRealDate(until) : null,
      start: start ? this.Utils.getRealDate(start) : null,
    };

    const result = { _originalUnavailabilities, _originalDetails };
    result.reset = function reset() {
      this.unavailabilities = angular.copy(this._originalUnavailabilities);
      this.details = angular.copy(this._originalDetails);
      this._editing = false;
      this._edited = false;
    };

    result.isEmpty = function isEmpty() {
      return !Object.keys(this.unavailabilities).some(
        (key) => this.unavailabilities[key].filter(inVisibleRange.bind(self)).length
      );
    };

    function inVisibleRange(availabilityEvent) {
      if (availabilityEvent.fullDay) {
        return true;
      }

      // spread here again in case settings changes meanwhile
      const { visibleHoursStart: _visibleHoursStart, visibleHoursEnd: _visibleHoursEnd } =
        this.Authentication.user.orgSettings;
      const { visibleStart, visibleEnd } = this.Utils.getVisibleHoursBoundaries(
        _visibleHoursStart,
        _visibleHoursEnd,
        availabilityEvent.dtstart
      );

      return !(availabilityEvent.dtend <= visibleStart || availabilityEvent.dtstart >= visibleEnd);
    }

    return result;
  }

  isApprovalEnabled() {
    return this.Authentication.user.orgSettings.unavailabilitySetApprovalEnabled;
  }

  isEditable(unavailabilitySet) {
    return (
      this.Authentication.user.isManager ||
      !this.isApprovalEnabled() ||
      !unavailabilitySet?.details?.approved
    );
  }

  isRequest(unavailabilitySet) {
    if (!unavailabilitySet) {
      return false;
    }
    const cancelled =
      'details' in unavailabilitySet
        ? unavailabilitySet.details.cancelled
        : unavailabilitySet.cancelled;
    if (cancelled) {
      return false;
    }

    const approved =
      'details' in unavailabilitySet
        ? unavailabilitySet.details.approved
        : unavailabilitySet.approved;
    return this.isApprovalEnabled() && !approved;
  }

  isEditing(sets) {
    return sets?.find((s) => s._editing);
  }

  shouldRequest() {
    return !this.Authentication.user.isManager && this.isApprovalEnabled();
  }

  canSaveSets(sets) {
    return sets && !sets.find((s) => s._editing && s.isEmpty());
  }

  showRequestConfirmModal(confirmationTitle, confirmationSubtitle, confirmationClass) {
    let timeout = null;

    const modal = this.$uibModal.open({
      size: '500',
      templateUrl: availabilityRequestedModalHtml,
      controllerAs: 'ctrl',
      controller() {
        'ngInject';

        this.confirmationTitle = confirmationTitle;
        this.confirmationSubtitle = confirmationSubtitle;
        this.confirmationClass = confirmationClass;
      },
    });

    modal.closed.then(() => timeout && clearTimeout(timeout));
    timeout = setTimeout(() => modal.close(), MODAL_CONFIRMATION_STATE_DELAY);
  }

  async approveUnavailabilitySet(set) {
    let setToSave = this.getUnavailabilityPayload(set);
    setToSave.approved = true;
    setToSave = this.transformUnavailabilityForSave(setToSave);

    const result = await this.SlingApi.approveUnavailability(setToSave);
    this.processUnavailabilitySetResponse(set, result);
    this.refreshUnavailabilities();

    this.showRequestConfirmModal(
      'You have approved this unavailability',
      'Employee will be notified',
      'success'
    );

    return result;
  }

  async denyUnavailabilitySet(set) {
    if (!set.availabilityId) {
      return;
    }

    await this.SlingApi.denyUnavailability(set.availabilityId);
    this.processUnavailabilitySetResponse(set, { deleted: true });
    this.refreshUnavailabilities();

    this.showRequestConfirmModal(
      'You have denied this unavailability',
      'Employee will be notified',
      'danger'
    );
  }

  async deleteAvailabilitySet(setId) {
    await this.Restangular.all(`availability/${setId}`).customDELETE();
    this.refreshUnavailabilities();
  }

  transformUnavailabilityForSave(unavailabilitySet) {
    if (unavailabilitySet.users) {
      if (unavailabilitySet.users.length) {
        unavailabilitySet.user = unavailabilitySet.users[0];
        unavailabilitySet.userId = unavailabilitySet.users[0].id;
      } else {
        unavailabilitySet.user = null;
        unavailabilitySet.userId = 0;
      }

      delete unavailabilitySet.users;
    }

    // Send timezone-less times!
    unavailabilitySet.dtstart = this.dateFilter(unavailabilitySet.dtstart, 'yyyy-MM-ddTHH:mm');
    unavailabilitySet.dtend = this.dateFilter(unavailabilitySet.dtend, 'yyyy-MM-ddTHH:mm');

    return unavailabilitySet;
  }
}

export default AvailabilityUtils;
